import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { trpc } from "../lib/trpc";
import { SearchSelect } from "../components/ui/SearchSelect";
import { ToggleSwitch } from "../components/ui/ToggleSwitch";
import { Icon } from "../components/ui/Icon";
// Durum etiketleri + URL okuma/yazma + süzgeç girdisi Siparişler ekranıyla TEK kaynaktan.
import {
  type SiparisDurumu, DURUM_SECENEKLERI, durumEtiketi, durumlariSirala, durumSecimOzeti, durumSuzgecGirdisi,
  durumlariUrldenOku, durumKunyesi, taninmayanDurumNotu, ACIK_SIPARISLER_AD, ACIK_SIPARISLER_KUME, acikSiparislerSecili, acikSiparislerKisayolu,
  turSecenekleri, turSecimOzeti, turSuzgecGirdisi, turleriUrldenOku,
} from "../lib/siparisDurum";

/**
 * TESLİMAT PLANI — A4 baskı sayfası.
 *
 * Siparişler sayfasındaki süzgeçler query string ile gelir ve BAŞLANGIÇ değeri olur; sonrasında
 * kullanıcı bu ekrandan tarih aralığı / müşteri / durum / aramayı DEĞİŞTİREBİLİR (ProductionReportPrint
 * ile aynı kalıp: URL yalnız tohumlar, state kullanıcıya aittir).
 *
 * Veri `order.deliveryPlan` ucundan gelir; o uç sipariş listesiyle AYNI `where`'i kullanır (ayna kuralı)
 * → bu rapor, listenin gizlediği bir siparişi gösteremez.
 *
 * ⚠ Sabit hex paleti bilinçli (diğer 7 baskı sayfasıyla aynı): belge teması ekran temasına bağlı olmamalı.
 */

const GUN_ADI = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];
const PARA_SIM: Record<string, string> = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };

/** Para — daima 2 hane, TR biçim. Tutarların KAYNAĞI uçtaki Decimal toplamıdır. */
function pf(v: number | null | undefined): string {
  if (v == null) return "—";
  return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
}

function nf(v: number | null | undefined, dec = 0): string {
  if (v == null) return "—";
  return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: dec, maximumFractionDigits: 2 }).format(v);
}
/** "2026-08-20" → "20.08.2026" (metin; Date'e çevirip saat dilimi kaydırmaya GEREK YOK). */
function fmtGun(ymd: string): string {
  const [y, m, d] = ymd.split("-");
  return `${d}.${m}.${y}`;
}
/** Gün adı — tarih-only değeri UTC'den okur (kayıt UTC gece yarısı olarak saklanıyor). */
function gunAdi(ymd: string): string {
  const dt = new Date(`${ymd}T00:00:00.000Z`);
  return Number.isNaN(dt.getTime()) ? "" : GUN_ADI[dt.getUTCDay()]!;
}

export function DeliveryPlanPrintPage() {
  const [sp] = useSearchParams();
  // URL yalnız BAŞLANGIÇ değerini verir; sonrasında state kullanıcıya aittir.
  const [dateFrom, setDateFrom] = useState(sp.get("dateFrom") ?? "");
  const [dateTo, setDateTo] = useState(sp.get("dateTo") ?? "");
  /**
   * ÇOK SEÇİMLİ durum: URL `statuses=a,b`; eski yer imlerindeki tekil `status=` de okunur
   * (ikisi birlikteyse BİRLEŞİM — sunucuyla aynı kural, `durumlariUrldenOku`).
   * Tanınmayan URL değeri SESSİZCE düşmez: `urlTaninmayan` ekranda + künyede söylenir; kullanıcı
   * durum seçimini değiştirince bağlantının tohumu artık geçerli olmadığı için temizlenir.
   */
  const [urlDurum] = useState(() => durumlariUrldenOku(sp));
  const [statuses, setStatusesHam] = useState<SiparisDurumu[]>(urlDurum.durumlar);
  const [urlTaninmayan, setUrlTaninmayan] = useState<string[]>(urlDurum.taninmayan);
  const setStatuses = (v: SiparisDurumu[]) => { setStatusesHam(v); setUrlTaninmayan([]); };
  /** ÜRÜN TÜRÜ: URL `itemTypeIds=a,b` + `turAtanmamis=1`. */
  const [turler, setTurler] = useState<string[]>(() => turleriUrldenOku(sp));
  // ÇOK SEÇİMLİ müşteri: URL'de virgülle ayrılır (tek müşteri de aynı biçimde taşınır).
  const [customerIds, setCustomerIds] = useState<string[]>(() => (sp.get("customerIds") ?? "").split(",").filter(Boolean));
  const [q, setQ] = useState(sp.get("q") ?? "");
  /**
   * FİNANSAL VERİ — bilinçli olarak KAPALI başlar; kullanıcı açarsa anlaşmalı birim fiyatlar
   * ve tutarlar rapora girer. Kapalıyken uç para yükü ÜRETMEZ (gereksiz ifşa yok).
   */
  const [finansal, setFinansal] = useState(false);
  /** Kullanıcının ELLE girdiği kurlar: { USD: "34,15", … } — TL karşılığı BİLGİ AMAÇLIDIR. */
  const [kurlar, setKurlar] = useState<Record<string, string>>({});

  const gecerliAralik = !dateFrom || !dateTo || dateFrom <= dateTo;

  const planQ = trpc.order.deliveryPlan.useQuery(
    {
      q: q.trim() || undefined,
      ...durumSuzgecGirdisi(statuses),
      ...turSuzgecGirdisi(turler),
      customerIds: customerIds.length ? customerIds : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      finansal,
    },
    { enabled: gecerliAralik },
  );
  const d = planQ.data;

  const musteriQ = trpc.customer.list.useQuery({ page: 1, pageSize: 100 }, { staleTime: 5 * 60_000 });
  const musteriler = useMemo(
    () => (musteriQ.data?.rows ?? []).map((c) => ({ id: c.id, name: c.name })).sort((a, b) => a.name.localeCompare(b.name, "tr")),
    [musteriQ.data],
  );
  // Süzgeçteki müşteri ilk 100'de yoksa seçili değer kaybolmasın (sessiz düşüm YOK).
  const musteriEksik = (musteriQ.data?.total ?? 0) > musteriler.length;
  const secilenYok = customerIds.some((id) => !musteriler.some((m) => m.id === id));

  // Ürün türü adları (seçenekler + künye). "Noodle/Tortilla" KODLANMAZ — Kart Türleri'nden gelir.
  const turQ = trpc.settings.itemType.list.useQuery({ kind: "finished" }, { staleTime: 5 * 60_000 });
  const turListesi = useMemo(() => (turQ.data ?? []).map((t) => ({ id: t.id, code: t.code, name: t.name })), [turQ.data]);
  const acikSecili = acikSiparislerSecili(statuses);

  const profQ = trpc.settings.companyProfile.get.useQuery({}, { staleTime: 5 * 60_000 });
  const prof = profQ.data;
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const ben = meQ.data?.user ?? null;
  const hazirlayan = ben ? `${ben.firstName ?? ""} ${ben.lastName ?? ""}`.trim() || ben.email : "—";

  const kapsam = [
    dateFrom || dateTo ? `Teslim ${dateFrom ? fmtGun(dateFrom) : "…"} – ${dateTo ? fmtGun(dateTo) : "…"}` : "Tüm teslim tarihleri",
    // Künye seçili durumları/türleri ETİKETLERİYLE basar (kâğıtta "3 durum" bilgi taşımaz).
    // Bağlantıdaki tanınmayan durum da künyede SÖYLENİR (süzgeç yok sayıldıysa "Tüm durumlar").
    durumKunyesi(statuses, urlTaninmayan),
    turler.length ? `Ürün türü: ${turSecimOzeti(turler, turListesi, { tam: true })}` : null,
    customerIds.length === 0 ? null
      : customerIds.length === 1 ? (musteriler.find((m) => m.id === customerIds[0])?.name ?? "Seçili müşteri")
      : `${customerIds.length} müşteri`,
    q.trim() ? `Arama: “${q.trim()}”` : null,
    finansal ? "Finansal veri dahil" : null,
  ].filter(Boolean).join(" · ");

  /** Raporda GEÇEN döviz kodları (TRY hariç) — kur kutuları yalnız bunlar için açılır. */
  const dovizler = useMemo(
    () => (d?.ozet.paraToplam ?? []).map((t) => t.paraBirimi).filter((c) => c !== "TRY"),
    [d],
  );
  /** "34,15" → 34.15 (TR ondalık virgülü kabul edilir). Geçersiz/boş → null. */
  const kurSayi = (c: string): number | null => {
    const ham = (kurlar[c] ?? "").trim().replace(",", ".");
    if (!ham) return null;
    const n = Number(ham);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  /**
   * TL KARŞILIĞI — CLAUDE.md: dövizli tutarlar TEK rakama İNDİRGENMEZ; bu yalnız BİLGİ AMAÇLI
   * alt toplamdır. Kuru GİRİLMEMİŞ döviz toplama KATILMAZ ve ayrıca SÖYLENİR (sessiz eksik yok).
   */
  const tlKarsilik = (paralar: Array<{ paraBirimi: string; tutar: number }>) => {
    let toplam = 0;
    const eksik: string[] = [];
    for (const t of paralar) {
      if (t.paraBirimi === "TRY") { toplam += t.tutar; continue; }
      const k = kurSayi(t.paraBirimi);
      if (k == null) { eksik.push(t.paraBirimi); continue; }
      toplam += t.tutar * k;
    }
    return { toplam, eksik };
  };

  return (
    <div className="dpp-root">
      <style>{`
        .dpp-root { --ink:#14253E; --line:#d7dce5; --muted:#5b6472; --bg-soft:#f2f5fa; background:#fff; color:#1a2230; }
        .dpp-page { max-width: 800px; margin: 0 auto; padding: 20px 24px 40px; font-family: 'Montserrat', system-ui, sans-serif; font-size: 12px; }
        .dpp-head { display:flex; flex-direction:column; border-bottom: 2px solid var(--ink); padding-bottom: 10px; margin-bottom: 14px; }
        .dpp-head-top { display:flex; align-items:flex-start; justify-content:space-between; }
        .dpp-title { font-size: 20px; font-weight: 800; color: var(--ink); letter-spacing:-0.01em; }
        .dpp-sub { font-size: 11px; color: var(--muted); margin-top: 2px; }
        .dpp-coblock { display:flex; align-items:center; gap:12px; }
        .dpp-co { text-align:right; font-size: 11px; color: var(--muted); }
        .dpp-co b { color: var(--ink); font-size: 13px; }
        .dpp-logo { max-height: 52px; max-width: 130px; object-fit: contain; }
        .dpp-head-meta { display:flex; flex-wrap:wrap; gap: 6px 18px; margin-top: 10px; padding-top: 8px; border-top: 1px solid var(--line); font-size: 11.5px; color: var(--muted); }
        .dpp-head-meta b { color: var(--ink); font-weight: 700; }
        .dpp-kpis { display:grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 14px; }
        .dpp-kpi { border:1px solid var(--line); border-radius:6px; padding:8px 10px; background: var(--bg-soft); }
        .dpp-kpi .l { font-size: 9.5px; font-weight:700; letter-spacing:.04em; text-transform:uppercase; color: var(--muted); }
        .dpp-kpi .v { font-size: 18px; font-weight:800; color: var(--ink); font-family:'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
        .dpp-kpi .u { font-size: 10px; font-weight:600; color: var(--muted); margin-left:3px; }
        .dpp-day { margin-top: 16px; }
        .dpp-day-h { display:flex; align-items:baseline; gap:10px; font-size: 13px; font-weight:800; color: var(--ink); padding-bottom:3px; border-bottom:1px solid var(--line); }
        .dpp-day-h .wd { font-size: 11px; font-weight:600; color: var(--muted); }
        .dpp-day-h .cnt { margin-left:auto; font-size: 10.5px; font-weight:600; color: var(--muted); }
        table.dpp-t { width:100%; border-collapse: collapse; font-size: 11px; }
        table.dpp-t th { text-align:left; font-size: 9.5px; font-weight:700; letter-spacing:.03em; text-transform:uppercase; color: var(--muted); border-bottom:1.5px solid var(--line); padding:5px 6px; }
        table.dpp-t td { padding:4px 6px; border-bottom:1px solid #eef1f6; vertical-align: top; }
        table.dpp-t td.n, table.dpp-t th.n { text-align:right; font-family:'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
        table.dpp-t td.ord { border-right:1px solid var(--line); }
        table.dpp-t tfoot td { font-weight:800; border-top:1.5px solid var(--ink); border-bottom:none; color: var(--ink); }
        .dpp-code { font-family:'JetBrains Mono', monospace; font-size: 9.5px; color: var(--muted); display:block; }
        /* Tahmini teslim tarihi işareti — BASKIDA renk güvenilmez, işaret METİNDİR. */
        .dpp-tahmin { font-weight: 800; color: #B45309; }
        .dpp-legend { margin-top: 10px; font-size: 10px; color: var(--muted); border-top: 1px solid var(--line); padding-top: 6px; }
        /* MÜŞTERİ ÖZETİ — gün tablolarından sonra gelen kırılım. Baskıda bir müşterinin
           bloğu mümkün olduğunca sayfa bölmez. */
        .dpp-sec-h { margin-top: 22px; padding-bottom: 3px; border-bottom: 2px solid var(--ink); font-size: 13px; font-weight: 800; color: var(--ink); display:flex; align-items:baseline; gap:10px; }
        .dpp-sec-h .s { font-size: 10.5px; font-weight: 600; color: var(--muted); margin-left:auto; }
        .dpp-cust { margin-top: 12px; break-inside: avoid; page-break-inside: avoid; }
        .dpp-cust-h { display:flex; align-items:baseline; gap:10px; font-size: 12px; font-weight:800; color: var(--ink); padding-bottom:3px; border-bottom:1px solid var(--line); }
        .dpp-cust-h .cnt { margin-left:auto; font-size: 10.5px; font-weight:600; color: var(--muted); }
        /* TUTAR KARTI — üç KPI'ın ALTINDA, TAM GENİŞLİKTE tek kart; dövizler ALT ALTA değil
           YAN YANA durur (kullanıcı yerleşimi, 15 Ağu 2026). Dar ekranda sarar. */
        .dpp-money-card { border:1px solid var(--line); border-radius:6px; padding:8px 10px; background: var(--bg-soft); margin-bottom: 14px; }
        .dpp-money-card .l { font-size: 9.5px; font-weight:700; letter-spacing:.04em; text-transform:uppercase; color: var(--muted); }
        .dpp-money-card .mc-row { display:flex; flex-wrap:wrap; align-items:baseline; gap: 6px 16px; margin-top: 2px; }
        .dpp-money-card .mc-list { display:flex; flex-wrap:wrap; align-items:baseline; }
        .dpp-money-card .mc-item { display:inline-flex; align-items:baseline; gap:3px; padding: 0 16px; border-right:1px solid var(--line); }
        .dpp-money-card .mc-item:first-child { padding-left: 0; }
        .dpp-money-card .mc-item:last-child { border-right:none; padding-right:0; }
        /* TL karşılığı: rakam DÖVİZLERLE AYNI boyutta (18px mono), satırın SAĞINA yaslı.
           "bilgi amaçlı" etiketi küçük kalır — tutar türetilmiştir, kaynak rakam soldakilerdir. */
        .dpp-money-card .mc-tl { margin-left:auto; display:inline-flex; align-items:baseline; gap:4px; text-align:right; }
        .dpp-money-card .mc-item b, .dpp-money-card .mc-tl b { font-size: 18px; font-weight:800; color: var(--ink); font-family:'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
        .dpp-money-card .mc-item .u, .dpp-money-card .mc-tl .u { font-size: 10px; font-weight:600; color: var(--muted); }
        .dpp-money-card .mc-tl .yak { font-size: 13px; font-weight:700; color: var(--muted); font-family:'JetBrains Mono', monospace; }
        .dpp-money-card .mc-tl .not { font-size: 9.5px; font-weight:600; color: var(--muted); }
        .dpp-money { font-family:'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
        .dpp-kur { display:flex; gap:8px; align-items:flex-end; flex-wrap:wrap; }
        .dpp-kur input { width: 92px; }
        .dpp-fin-note { font-size: 10px; color: var(--muted); margin-top: 2px; }
        .dpp-note { border:1px solid #e6c88a; border-left:4px solid #B45309; background:#fdf8ef; border-radius:6px; padding:8px 12px; font-size:11.5px; color:#3b3226; margin: 10px 0; }
        .dpp-empty { text-align:center; color: var(--muted); padding: 40px 0; }
        .dpp-toolbar { position: sticky; top: 0; z-index: 5; background:#fff; display:flex; gap:12px; align-items:flex-end; justify-content:space-between; flex-wrap:wrap; padding: 10px 24px; border-bottom:1px solid var(--line); }
        .dpp-filt { display:flex; gap:10px; align-items:flex-end; flex-wrap:wrap; }
        .dpp-filt label { display:flex; flex-direction:column; gap:3px; font-size:10px; font-weight:700; letter-spacing:.03em; text-transform:uppercase; color: var(--muted); }
        .dpp-filt input, .dpp-filt select { font-family:inherit; font-size:12.5px; padding:5px 8px; border:1px solid var(--line); border-radius:5px; background:#fff; color:#1a2230; }
        .dpp-tb-actions { display:flex; gap:8px; align-items:flex-end; padding-bottom:1px; }
        .dpp-btn { border:1px solid var(--ink); background: var(--ink); color:#fff; font-weight:600; font-size:13px; padding:7px 16px; border-radius:6px; cursor:pointer; }
        .dpp-btn.sec { background:#fff; color: var(--ink); }
        .dpp-btn:disabled { opacity:.5; cursor:default; }
        /* Çoklu seçim kutuları (SearchSelect) etiket sütununda dar kalmasın. */
        .dpp-filt label.dpp-ss { min-width: 190px; }
        .dpp-filt label.dpp-ss.genis { min-width: 220px; }
        /* Etiketin BÜYÜK HARF/kalın yazısı tetikleyici metnine miras kalmasın. */
        .dpp-filt label.dpp-ss .ss-trigger { text-transform: none; letter-spacing: 0; font-weight: 500; }
        /* "Açık siparişler" kısayolu — basılıyken dolu + onay ikonu (renk tek başına taşımaz). */
        .dpp-kisayol { display:inline-flex; align-items:center; gap:4px; font-size: 12px; padding: 6px 12px; }
        .dpp-kisayol.on { background: var(--ink); color: #fff; }
        @media print {
          .dpp-toolbar { display:none !important; }
          .dpp-page { max-width:none; padding:0; }
          table.dpp-t { font-size: 10px; }
          .dpp-head, .dpp-day-h { break-after: avoid; }
          .dpp-day { break-inside: auto; }
          tr { break-inside: avoid; }
          thead { display: table-header-group; }
        }
        @page { size: A4 portrait; margin: 12mm; }
      `}</style>

      <div className="dpp-toolbar">
        <div className="dpp-filt">
          <label>Teslim Başlangıç<input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} /></label>
          <label>Teslim Bitiş<input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} /></label>
          <label className="dpp-ss">Durum
            <SearchSelect
              value={null}
              multiValues={statuses}
              onChangeMulti={(v) => setStatuses(durumlariSirala(v))}
              multiSummary={() => durumSecimOzeti(statuses)}
              onChange={() => {}}
              options={DURUM_SECENEKLERI}
              placeholder="Tüm Durumlar"
              searchPlaceholder="Durum ara…"
              showFooter={false}
              emptyText="—"
            />
          </label>
          {/* Siparişler ekranı ve MRP seçicisiyle AYNI kısayol (aç/kapa). */}
          <button
            type="button"
            className={`dpp-btn sec dpp-kisayol${acikSecili ? " on" : ""}`}
            aria-pressed={acikSecili}
            onClick={() => setStatuses(acikSiparislerKisayolu(statuses))}
            title={acikSecili ? "Kısayolu kaldır — tüm durumlar" : `Yalnız ${ACIK_SIPARISLER_KUME} durumundaki siparişler`}
          >
            {acikSecili ? <Icon name="check" size={12} /> : null}{ACIK_SIPARISLER_AD} ({ACIK_SIPARISLER_KUME})
          </button>
          <label className="dpp-ss">Ürün Türü
            <SearchSelect
              value={null}
              multiValues={turler}
              onChangeMulti={setTurler}
              multiSummary={() => turSecimOzeti(turler, turListesi)}
              onChange={() => {}}
              options={turSecenekleri(turListesi)}
              placeholder={turQ.isError ? "Türler yüklenemedi" : "Tüm Türler"}
              searchPlaceholder="Tür ara…"
              emptyText={turQ.isLoading ? "Yükleniyor…" : turQ.isError ? `Türler yüklenemedi: ${turQ.error.message}` : "Tür bulunamadı"}
              error={turQ.isError}
            />
          </label>
          <label className="dpp-ss genis">Müşteri
            <SearchSelect
              value={null}
              multiValues={customerIds}
              onChangeMulti={setCustomerIds}
              onChange={() => {}}
              options={musteriler.map((m) => ({ value: m.id, label: m.name }))}
              placeholder="Tüm Müşteriler"
              searchPlaceholder="Müşteri ara…"
              emptyText="Müşteri bulunamadı"
              multiSummary={(n) => `${n} müşteri seçili`}
            />
          </label>
          <label>Ara<input type="text" value={q} placeholder="Sipariş / müşteri" onChange={(e) => setQ(e.target.value)} /></label>
          <label>Finansal Veri
            <ToggleSwitch checked={finansal} onChange={setFinansal} ariaLabel="Finansal veriyi rapora ekle" />
          </label>
          {/* Kur kutuları YALNIZ raporda gerçekten geçen dövizler için — boş kutu gürültüsü yok. */}
          {finansal && dovizler.length > 0 ? (
            <label>Kur (1 birim = ? ₺)
              <span className="dpp-kur">
                {dovizler.map((c) => (
                  <input
                    key={c}
                    type="text"
                    inputMode="decimal"
                    value={kurlar[c] ?? ""}
                    placeholder={`${c} kuru`}
                    aria-label={`${c} için TL kuru`}
                    onChange={(e) => setKurlar((k) => ({ ...k, [c]: e.target.value }))}
                  />
                ))}
              </span>
            </label>
          ) : null}
        </div>
        <div className="dpp-tb-actions">
          <button type="button" className="dpp-btn sec" onClick={() => window.close()}>Kapat</button>
          <button type="button" className="dpp-btn" onClick={() => window.print()} disabled={!d}>Yazdır</button>
        </div>
      </div>

      <div className="dpp-page">
        {!gecerliAralik ? (
          <div className="dpp-note">Başlangıç tarihi bitişten sonra olamaz.</div>
        ) : planQ.isLoading ? (
          <div className="dpp-empty">Yükleniyor…</div>
        ) : planQ.isError ? (
          <div className="dpp-note">Rapor yüklenemedi: {planQ.error?.message}</div>
        ) : !d ? null : (
          <>
            <div className="dpp-head">
              <div className="dpp-head-top">
                <div>
                  <div className="dpp-title">Teslimat Planı</div>
                  <div className="dpp-sub">{kapsam}</div>
                </div>
                <div className="dpp-coblock">
                  <div className="dpp-co">
                    {prof?.name ? <b>{prof.name}</b> : <b>STEPERP</b>}
                    {prof?.address ? <div>{prof.address}</div> : null}
                  </div>
                  {prof?.logoUrl ? <img className="dpp-logo" src={prof.logoUrl} alt={prof.name ?? "Logo"} /> : null}
                </div>
              </div>
              <div className="dpp-head-meta">
                <span>Hazırlayan: <b>{hazirlayan}</b></span>
                <span>Sipariş: <b>{nf(d.ozet.siparisSayisi)}</b></span>
                <span>Teslim günü: <b>{nf(d.ozet.gunSayisi)}</b></span>
              </div>
            </div>

            {/* Bağlantıdaki tanınmayan durum — sessizce "tüm durumlar"a düşülmez; not baskıda da kalır. */}
            {taninmayanDurumNotu(urlTaninmayan) ? (
              <div className="dpp-note" role="alert">
                {taninmayanDurumNotu(urlTaninmayan)}{" "}
                {statuses.length ? "Süzgeç yalnız tanınan durumlarla uygulandı." : "Bu yüzden TÜM durumlar listeleniyor."}
                {" "}Durum kutusundan seçim yapın.
              </div>
            ) : null}
            {d.tarihsizGizli > 0 ? (
              <div className="dpp-note">
                <b>{nf(d.tarihsizGizli)}</b> siparişin teslim tarihi girilmemiş; tarih aralığı süzgeci açık olduğu için bu planda yer almıyor.
                Görmek için tarih aralığını temizleyin.
              </div>
            ) : null}
            {/* Müşteri listesi ilk 100 ile sınırlı — seçim yaparken bunu BİLMEK gerekir
                (sessiz kırpma yok). `secilenYok`: URL'den gelen bir müşteri o 100'de değil. */}
            {musteriEksik || secilenYok ? (
              <div className="dpp-note">
                Müşteri süzgeci listesi ilk <b>{nf(musteriler.length)}</b> müşteriyi gösteriyor
                (toplam <b>{nf(musteriQ.data?.total ?? 0)}</b>).
                {secilenYok ? " Seçili müşterilerden bazıları bu listede görünmüyor; süzgeç yine de onları kapsıyor." : ""}
                {" "}Aradığınız müşteri listede yoksa arama kutusunu kullanın.
              </div>
            ) : null}
            {finansal && d.ozet.fiyatsizSatir > 0 ? (
              <div className="dpp-note">
                <b>{nf(d.ozet.fiyatsizSatir)}</b> sipariş satırında anlaşmalı birim fiyat yok; bu satırlar
                tutar toplamlarına <b>dahil edilmedi</b>. Fiyat, sipariş açılırken müşteri anlaşmasından donar —
                anlaşması olmayan kalemde boş kalır.
              </div>
            ) : null}
            {d.kirpildi ? (
              <div className="dpp-note">
                Süzgece uyan sipariş sayısı {nf(d.kapak)} sınırını aştı; plan ilk {nf(d.kapak)} siparişi gösteriyor. Aralığı daraltın.
              </div>
            ) : null}

            <div className="dpp-kpis">
              <div className="dpp-kpi"><div className="l">Sipariş</div><div className="v">{nf(d.ozet.siparisSayisi)}</div></div>
              <div className="dpp-kpi"><div className="l">Teslim Günü</div><div className="v">{nf(d.ozet.gunSayisi)}</div></div>
              {/* Rapor "hangi gün ne teslim edilecek"i SİPARİŞ miktarıyla anlatır; kalan/sevk
                  edilen ayrımı sahada karışıklık yarattığı için kaldırıldı (15 Ağu 2026). */}
              <div className="dpp-kpi">
                <div className="l">Sipariş Miktarı</div>
                <div className="v">
                  {d.ozet.toplam.length === 0 ? "—" : d.ozet.toplam.map((t) => (
                    <div key={t.uom}>{nf(t.qty)}<span className="u">{t.uom}</span></div>
                  ))}
                </div>
              </div>
            </div>

            {/* TUTAR — üç KPI'ın altında AYRI ve TAM GENİŞLİKTE tek satır; her döviz yan yana
                ayrı bölme. Dövizler tek rakama İNDİRGENMEZ; TL satırı türetilmiş ve bilgi amaçlıdır. */}
            {finansal ? (
              <div className="dpp-money-card">
                <div className="mc-head"><span className="l">Tutar (Anlaşmalı)</span></div>
                <div className="mc-row">
                  <span className="mc-list">
                    {d.ozet.paraToplam.length === 0 ? (
                      <span className="mc-item"><b>—</b></span>
                    ) : d.ozet.paraToplam.map((t) => (
                      <span className="mc-item" key={t.paraBirimi}>
                        <b>{pf(t.tutar)}</b>
                        <span className="u">{PARA_SIM[t.paraBirimi] ?? t.paraBirimi}</span>
                      </span>
                    ))}
                  </span>
                  {d.ozet.paraToplam.length > 0 ? (() => {
                    const { toplam, eksik } = tlKarsilik(d.ozet.paraToplam);
                    return (
                      <span className="mc-tl">
                        <span className="yak">≈</span>
                        <b>{pf(toplam)}</b>
                        <span className="u">₺</span>
                        <span className="not">
                          (bilgi amaçlı){eksik.length ? ` · ${eksik.join(", ")} kuru girilmedi, dahil DEĞİL` : ""}
                        </span>
                      </span>
                    );
                  })() : null}
                </div>
              </div>
            ) : null}

            {d.gunler.length === 0 && !d.tarihsiz ? (
              <div className="dpp-empty">Süzgece uyan teslimat bulunamadı.</div>
            ) : null}

            {[...d.gunler.map((g) => ({ baslik: fmtGun(g.tarih), alt: gunAdi(g.tarih), ...g })),
              ...(d.tarihsiz ? [{ baslik: "Teslim tarihi girilmemiş", alt: "", tarih: "", ...d.tarihsiz }] : [])
            ].map((g) => (
              <div className="dpp-day" key={g.tarih || "tarihsiz"}>
                <div className="dpp-day-h">
                  <span>{g.baslik}</span>
                  {g.alt ? <span className="wd">{g.alt}</span> : null}
                  <span className="cnt">{nf(g.siparisler.length)} sipariş</span>
                </div>
                <table className="dpp-t">
                  <thead>
                    <tr>
                      <th style={{ width: 130 }}>Sipariş</th>
                      <th style={{ width: 150 }}>Müşteri</th>
                      <th>Ürün</th>
                      <th className="n" style={{ width: 110 }}>Sipariş</th>
                      {finansal ? <th className="n" style={{ width: 92 }}>Birim Fiyat</th> : null}
                      {finansal ? <th className="n" style={{ width: 104 }}>Tutar</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {g.siparisler.flatMap((o) =>
                      (o.kalemler.length ? o.kalemler : [{ code: "—", name: "(satır yok)", qty: 0, uom: "", birimFiyat: null, paraBirimi: null, tutar: null }]).map((k, i) => (
                        <tr key={`${o.id}-${k.code}-${k.uom}-${i}`}>
                          {i === 0 ? (
                            <>
                              <td className="ord" rowSpan={Math.max(1, o.kalemler.length)}>
                                {o.code}
                                {/* Söz verilen tarih yoksa istenen tarihe düşüldü — kâğıtta da SÖYLENİR;
                                    aksi halde tahmini gün, verilmiş taahhüt gibi okunur. */}
                                {o.teslimTahmini ? <span className="dpp-tahmin" title="Söz verilen teslim tarihi girilmemiş">&nbsp;*</span> : null}
                                <span className="dpp-code">{durumEtiketi(o.status)}</span>
                              </td>
                              <td className="ord" rowSpan={Math.max(1, o.kalemler.length)}>{o.customerName}</td>
                            </>
                          ) : null}
                          <td>{k.name}<span className="dpp-code">{k.code}</span></td>
                          <td className="n">{nf(k.qty)} {k.uom}</td>
                          {finansal ? (
                            <td className="n dpp-money">
                              {k.birimFiyat == null ? "—" : `${pf(k.birimFiyat)} ${PARA_SIM[k.paraBirimi ?? ""] ?? k.paraBirimi ?? ""}`}
                            </td>
                          ) : null}
                          {finansal ? (
                            <td className="n dpp-money">
                              {k.tutar == null ? "—" : `${pf(k.tutar)} ${PARA_SIM[k.paraBirimi ?? ""] ?? k.paraBirimi ?? ""}`}
                            </td>
                          ) : null}
                        </tr>
                      )),
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3}>GÜN TOPLAMI</td>
                      <td className="n">{g.toplam.map((t) => <div key={t.uom}>{nf(t.qty)} {t.uom}</div>)}</td>
                      {finansal ? <td className="n" /> : null}
                      {finansal ? (
                        <td className="n dpp-money">
                          {g.paraToplam.map((t) => (
                            <div key={t.paraBirimi}>{pf(t.tutar)} {PARA_SIM[t.paraBirimi] ?? t.paraBirimi}</div>
                          ))}
                          {g.paraToplam.length > 0 ? (() => {
                            const { toplam, eksik } = tlKarsilik(g.paraToplam);
                            return <div className="dpp-fin-note">≈ {pf(toplam)} ₺{eksik.length ? " (eksik kur)" : ""}</div>;
                          })() : null}
                        </td>
                      ) : null}
                    </tr>
                  </tfoot>
                </table>
              </div>
            ))}
            {/* ══ MÜŞTERİ ÖZETİ — "hangi müşteriye toplam ne kadar, hangi fiyattan, ne tutarda"
                Gün tablolarıyla AYNI süzgeç ve AYNI kayıtlardan türer (uçta tek geçişte). ══ */}
            {d.musteriOzet.length > 0 ? (
              <>
                <div className="dpp-sec-h">
                  <span>Müşteri Özeti</span>
                  <span className="s">{nf(d.musteriOzet.length)} müşteri</span>
                </div>
                {d.musteriOzet.map((m) => (
                  <div className="dpp-cust" key={m.ad}>
                    <div className="dpp-cust-h">
                      <span>{m.ad}</span>
                      <span className="cnt">{nf(m.siparisSayisi)} sipariş</span>
                    </div>
                    <table className="dpp-t">
                      <thead>
                        <tr>
                          <th>Ürün</th>
                          <th className="n" style={{ width: 110 }}>Miktar</th>
                          {finansal ? <th className="n" style={{ width: 92 }}>Birim Fiyat</th> : null}
                          {finansal ? <th className="n" style={{ width: 104 }}>Tutar</th> : null}
                        </tr>
                      </thead>
                      <tbody>
                        {m.kalemler.map((k, i) => (
                          <tr key={`${m.ad}-${k.code}-${k.uom}-${k.paraBirimi ?? ""}-${i}`}>
                            <td>{k.name}<span className="dpp-code">{k.code}</span></td>
                            <td className="n">{nf(k.qty)} {k.uom}</td>
                            {finansal ? (
                              <td className="n dpp-money">
                                {k.birimFiyat == null ? "—" : `${pf(k.birimFiyat)} ${PARA_SIM[k.paraBirimi ?? ""] ?? k.paraBirimi ?? ""}`}
                              </td>
                            ) : null}
                            {finansal ? (
                              <td className="n dpp-money">
                                {k.tutar == null ? "—" : `${pf(k.tutar)} ${PARA_SIM[k.paraBirimi ?? ""] ?? k.paraBirimi ?? ""}`}
                              </td>
                            ) : null}
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td>MÜŞTERİ TOPLAMI</td>
                          <td className="n">{m.toplam.map((t) => <div key={t.uom}>{nf(t.qty)} {t.uom}</div>)}</td>
                          {finansal ? <td className="n" /> : null}
                          {finansal ? (
                            <td className="n dpp-money">
                              {m.paraToplam.map((t) => (
                                <div key={t.paraBirimi}>{pf(t.tutar)} {PARA_SIM[t.paraBirimi] ?? t.paraBirimi}</div>
                              ))}
                              {m.paraToplam.length > 0 ? (() => {
                                const { toplam, eksik } = tlKarsilik(m.paraToplam);
                                return <div className="dpp-fin-note">≈ {pf(toplam)} ₺{eksik.length ? " (eksik kur)" : ""}</div>;
                              })() : null}
                              {/* Fiyatsız satır bu müşteride tutarı EKSİK bırakıyorsa SÖYLENİR. */}
                              {m.fiyatsizSatir > 0 ? (
                                <div className="dpp-fin-note">{nf(m.fiyatsizSatir)} satır fiyatsız — tutara dahil değil</div>
                              ) : null}
                            </td>
                          ) : null}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ))}
              </>
            ) : null}

            {/* Lejant — yalnız gerçekten tahmin varsa basılır (gereksiz gürültü yok). */}
            {[...d.gunler.flatMap((g) => g.siparisler), ...(d.tarihsiz?.siparisler ?? [])].some((s) => s.teslimTahmini) ? (
              <div className="dpp-legend">
                <span className="dpp-tahmin">*</span> Söz verilen teslim tarihi girilmemiş; <b>istenen tarih</b> baz alınmıştır.
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
